import { useState, useEffect, useCallback } from "react";
import { useToast } from "@chakra-ui/react";
import axios from "axios";
import { adminApi } from "../../../data/axios/adminApi";
import { AdminRole, AdminPermission } from "../../../types/admin";

const PAGE_SIZE = 20;
// page_size máx. aceito pelo back — o picker de permissões do form precisa de
// TODAS as permissões, não de uma página.
const ALL_PERMISSIONS_PAGE_SIZE = 200;

export function useRolesAdmin() {
  const toast = useToast();
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [allPermissions, setAllPermissions] = useState<AdminPermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<AdminRole | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);

  const fetchRolesAndPermissions = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);
      const [rolesData, permsData] = await Promise.all([
        adminApi.listRoles({ page, page_size: PAGE_SIZE }),
        adminApi.listPermissions({ page_size: ALL_PERMISSIONS_PAGE_SIZE }),
      ]);
      setRoles(rolesData.roles);
      setTotalPages(rolesData.total_pages);
      setAllPermissions(permsData.permissions);
    } catch {
      setError(true);
      toast({ title: "Erro ao carregar dados dos papéis", status: "error", duration: 4000, isClosable: true });
    } finally {
      setLoading(false);
    }
  }, [page, toast]);

  useEffect(() => {
    fetchRolesAndPermissions();
  }, [fetchRolesAndPermissions]);

  const handleCreateOpen = () => {
    setSelectedRole(null);
    setIsFormOpen(true);
  };

  const handleEditOpen = (role: AdminRole) => {
    setSelectedRole(role);
    setIsFormOpen(true);
  };

  const handleViewPermissionsOpen = (role: AdminRole) => {
    setSelectedRole(role);
    setIsDrawerOpen(true);
  };

  const handleDeleteClick = (id: number) => setDeleteTargetId(id);
  const cancelDelete = () => setDeleteTargetId(null);

  const confirmDelete = async () => {
    if (deleteTargetId === null) return;
    try {
      await adminApi.deleteRole(deleteTargetId);
      toast({ title: "Papel excluído com sucesso", status: "success", duration: 3000, isClosable: true });
      setDeleteTargetId(null);
      fetchRolesAndPermissions();
    } catch (error) {
      const detail = axios.isAxiosError(error) ? error.response?.data?.detail : undefined;
      toast({ title: detail || "Erro ao excluir papel", status: "error", duration: 5000, isClosable: true });
      setDeleteTargetId(null);
    }
  };

  return {
    roles,
    allPermissions,
    loading,
    error,
    page,
    totalPages,
    setPage,
    isFormOpen,
    setIsFormOpen,
    isDrawerOpen,
    setIsDrawerOpen,
    selectedRole,
    deleteTargetId,
    handleCreateOpen,
    handleEditOpen,
    handleViewPermissionsOpen,
    handleDeleteClick,
    confirmDelete,
    cancelDelete,
    refreshList: fetchRolesAndPermissions,
  };
}
